import { createTheme } from '@mui/material/styles';

/**
 * Vaqtly Holding — Duolingo-derived design tokens.
 *
 * The look is built from three ideas Duolingo uses everywhere:
 *   1. Flat, saturated colour. No gradients on controls, no soft drop shadows.
 *   2. A control is a physical object: it rests on a 4px solid edge of a darker
 *      shade of its own colour, and pressing it sinks the face onto that edge.
 *   3. Rounded everything (16px on controls and cards) set in a rounded typeface.
 *
 * Only `SIGNAL` needs to change to re-skin the whole site — every other blue is
 * derived from it by name, not copied.
 */

/** Duolingo's blue. Its green (#58CC02) is one edit away; see PALETTE below. */
const SIGNAL = '#1cb0f6';
/** The edge the blue sits on. Always a darker step of the face, never a grey. */
const SIGNAL_DEPTH = '#1899d6';

export const PALETTE = {
  signal: SIGNAL,
  signalDepth: SIGNAL_DEPTH,
  /** Duolingo hovers by lightening — a control that dims reads as disabled. */
  signalHover: '#3dc0fd',
  signalSoft: '#ddf4ff',

  /** Duolingo green. Success, progress, and the "live" product badge. */
  green: '#58cc02',
  greenDepth: '#46a302',
  greenSoft: '#d7ffb8',

  /** Streak gold, used only as a fill — at 1.7:1 it is never text. */
  gold: '#ffc800',
  goldDepth: '#e5a600',

  red: '#ff4b4b',
  redDepth: '#ea2b2b',

  /** Duolingo's neutrals: a near-black ink, a grey for support copy, a hairline. */
  ink: '#3c3c3c',
  inkMuted: '#777777',
  inkFaint: '#afafaf',
  hairline: '#e5e5e5',
  /** The edge under a white/outline control. The one grey depth in the system. */
  controlDepth: '#e5e5e5',

  ground: '#ffffff',
  groundSunken: '#f7f7f7',
} as const;

/** Radii live here so a button, a card and a badge can never drift apart. */
export const RADIUS = {
  control: 16,
  card: 16,
  pill: 999,
} as const;

/**
 * The press. Shared by every pressable surface so the gesture is identical
 * sitewide: the face drops by exactly the depth while the edge disappears, so
 * the bottom of the control stays put and only the face moves.
 *
 * Hover lifts *and* brightens, matching the animata reference's `brightness-110`.
 * The press itself is faster than the release (75ms down, 150ms back), which is
 * what makes it feel like a physical key rather than an animation.
 */
export const press = (depth: string) => ({
  boxShadow: `0 4px 0 0 ${depth}`,
  transition: 'transform 150ms ease, box-shadow 150ms ease, filter 150ms ease, background-color 150ms ease',
  '@media (hover: hover)': {
    '&:hover': {
      filter: 'brightness(1.1)',
      transform: 'translateY(-2px)',
      boxShadow: `0 6px 0 0 ${depth}`,
    },
  },
  '&:active': {
    filter: 'brightness(1)',
    transform: 'translateY(4px)',
    boxShadow: 'none',
    transitionDuration: '75ms',
  },
});

const theme = createTheme({
  palette: {
    primary: { main: PALETTE.signal, dark: PALETTE.signalDepth, light: PALETTE.signalSoft, contrastText: '#ffffff' },
    secondary: { main: PALETTE.green, dark: PALETTE.greenDepth, light: PALETTE.greenSoft, contrastText: '#ffffff' },
    error: { main: PALETTE.red, dark: PALETTE.redDepth },
    warning: { main: PALETTE.gold, dark: PALETTE.goldDepth },
    text: { primary: PALETTE.ink, secondary: PALETTE.inkMuted, disabled: PALETTE.inkFaint },
    divider: PALETTE.hairline,
    background: { default: PALETTE.ground, paper: PALETTE.ground },
  },

  typography: {
    /*
      Nunito. Duolingo sets its interface in Feather Bold, which is not licensable;
      Nunito is the free rounded geometric that carries the same warmth, and the
      rounded letterforms are most of why this style reads as friendly rather than
      corporate. It also covers Uzbek Latin and Cyrillic, which the content needs.
    */
    fontFamily: ['Nunito', 'ui-rounded', '-apple-system', 'Segoe UI', 'Roboto', 'sans-serif'].join(','),
    /* Duolingo headlines are heavy and tight; body copy is normal weight and loose. */
    h1: { fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1 },
    h2: { fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.15 },
    h3: { fontWeight: 800, letterSpacing: '-0.01em', lineHeight: 1.2 },
    h4: { fontWeight: 800, lineHeight: 1.25 },
    h5: { fontWeight: 700, lineHeight: 1.3 },
    h6: { fontWeight: 700, lineHeight: 1.4 },
    body1: { fontWeight: 500, lineHeight: 1.6 },
    body2: { fontWeight: 500, lineHeight: 1.6 },
    /* Every Duolingo button label is uppercase, heavy, and widely tracked. */
    button: { textTransform: 'uppercase', fontWeight: 800, letterSpacing: '0.06em' },
  },

  shape: { borderRadius: RADIUS.control },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: PALETTE.ground, WebkitFontSmoothing: 'antialiased' },
        /* Focus is never colour-only and follows the element's own radius. */
        ':focus-visible': { outline: `3px solid ${PALETTE.signal}`, outlineOffset: 2 },
      },
    },
    MuiButton: {
      /* The depth edge is drawn by hand, so MUI's own elevation must stay out. */
      defaultProps: { disableElevation: true, disableRipple: true },
      styleOverrides: {
        root: { borderRadius: RADIUS.control },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: RADIUS.card,
          backgroundImage: 'none',
        },
      },
    },
  },
});

export default theme;
